import { ExactStringUnionArray } from "helpers";
import { ReviewReason } from "src/store/api/generatedApi";

interface ReviewReasonConfig {
  reason: ReviewReason;
  themeColor: string;
  isConflict: boolean;
}

export const FLAG_OUTLINE_COLOR = "error.main";

export const highlightColorsByEventId = <T extends { id: string }>(
  events: readonly T[],
  getHighlightColor: (event: T) => string | null | undefined
): ReadonlyMap<string, string | null> =>
  new Map(events.map((event) => [event.id, getHighlightColor(event) ?? null]));

const REVIEW_REASON_PRIORITY: readonly ReviewReasonConfig[] = [
  { reason: "missing_labels", themeColor: "data.pink", isConflict: true },
  { reason: "incompatible_labels", themeColor: "error.main", isConflict: true },
  { reason: "invalid_sequence", themeColor: "error.main", isConflict: true },
  { reason: "ambiguous_matching", themeColor: "error.main", isConflict: true },
  { reason: "comment", themeColor: "error.main", isConflict: true },
  { reason: "too_few_examples", themeColor: "info.main", isConflict: true },
  {
    reason: "low_confidence_event",
    themeColor: "data.red",
    isConflict: false,
  },
  {
    reason: "low_confidence_material",
    themeColor: "data.red",
    isConflict: false,
  },
  {
    reason: "low_confidence_area",
    themeColor: "data.red",
    isConflict: false,
  },
  {
    reason: "confusable_material",
    themeColor: "data.orange",
    isConflict: false,
  },
  {
    reason: "unplanned_material",
    themeColor: "data.blue",
    isConflict: false,
  },
  {
    reason: "railcar_disagreement",
    themeColor: "data.green",
    isConflict: false,
  },
  {
    reason: "mass_outlier",
    themeColor: "data.pink",
    isConflict: false,
  },
  {
    reason: "duplicate_mass",
    themeColor: "data.pink",
    isConflict: false,
  },
  {
    reason: "likely_error",
    themeColor: "data.lightblue",
    isConflict: false,
  },
  {
    reason: "incompatible_timing",
    themeColor: "warning.main",
    isConflict: false,
  },
  { reason: "false_positive", themeColor: "error.main", isConflict: true },
  { reason: "false_negative", themeColor: "error.main", isConflict: true },
];

export const REVIEW_REASON_PRIORITY_VALUES = [
  "incompatible_labels",
  "missing_labels",
  "invalid_sequence",
  "ambiguous_matching",
  "too_few_examples",
  "comment",
  "incompatible_timing",
  "false_positive",
  "false_negative",
  "low_confidence_event",
  "low_confidence_material",
  "low_confidence_area",
  "confusable_material",
  "unplanned_material",
  "railcar_disagreement",
  "mass_outlier",
  "duplicate_mass",
  "likely_error",
] as const;

type CheckReviewReasons = ExactStringUnionArray<
  typeof REVIEW_REASON_PRIORITY_VALUES,
  ReviewReason
>;

true satisfies CheckReviewReasons;

export const TARGETED_REVIEW_REASONS: readonly ReviewReason[] = [
  "likely_error",
  "confusable_material",
  "unplanned_material",
  "railcar_disagreement",
  "mass_outlier",
  "duplicate_mass",
];

const conflictReasons = new Set(
  REVIEW_REASON_PRIORITY.filter((c) => c.isConflict).map((c) => c.reason)
);

const reasonPriorityMap = new Map(
  REVIEW_REASON_PRIORITY.map((config, index) => [
    config.reason,
    { ...config, priority: index },
  ])
);

export const formatReviewReason = (reason: ReviewReason): string => {
  const reasonMap: Record<ReviewReason, string> = {
    too_few_examples: "Too few examples",
    missing_labels: "Missing labels",
    incompatible_labels: "Incompatible labels",
    incompatible_timing: "Incompatible timing",
    ambiguous_matching: "Ambiguous matching",
    invalid_sequence: "Invalid sequence",
    false_positive: "False positive",
    false_negative: "False negative",
    comment: "Comment",
    low_confidence_event: "Low event confidence",
    low_confidence_material: "Low material confidence",
    low_confidence_area: "Low area confidence",
    likely_error: "Likely error",
    confusable_material: "Confusable material",
    unplanned_material: "Unplanned material",
    railcar_disagreement: "Railcar disagreement",
    mass_outlier: "Implausible mass",
    duplicate_mass: "Duplicate mass",
  };
  return reasonMap[reason];
};

export const TARGETED_REVIEW_REASON_LABEL_KEYS: Partial<
  Record<ReviewReason, string>
> = {
  likely_error: "reviewReasonLikelyError",
  confusable_material: "reviewReasonConfusableMaterial",
  unplanned_material: "reviewReasonUnplannedMaterial",
  railcar_disagreement: "reviewReasonRailcarDisagreement",
  mass_outlier: "reviewReasonMassOutlier",
  duplicate_mass: "reviewReasonDuplicateMass",
};

export const formatReviewReasons = (
  reasons: ReviewReason[] | undefined
): string => {
  if (!reasons || reasons.length === 0) return "";
  return reasons
    .filter((reason) => reason !== "likely_error")
    .map(formatReviewReason)
    .join(", ");
};

export const hasConflictReasons = (
  reasons: ReviewReason[] | undefined
): boolean => {
  if (!reasons || reasons.length === 0) return false;
  return reasons.some((r) => conflictReasons.has(r));
};

export const getHighestPriorityReasonConfig = (
  reasons: ReviewReason[] | undefined
): ReviewReasonConfig | undefined => {
  if (!reasons || reasons.length === 0) return undefined;
  let best: ({ priority: number } & ReviewReasonConfig) | undefined;
  for (const reason of reasons) {
    const config = reasonPriorityMap.get(reason);
    if (config && (best === undefined || config.priority < best.priority)) {
      best = config;
    }
  }
  return best;
};
